import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';

import VolumeConfiguration from './VolumeConfiguration';
import VolumeDistribution from './VolumeDistribution';
import AutomationConfiguration from './AutomationConfiguration';
import RateDistribution from './RateDistribution';
import SupportHeadcount from './SupportHeadcount';
import AttendanceOverride from './AttendanceOverride';
import ResultsView from './ResultsView';
import Alert from '@amzn/meridian/alert';
import Modal from '@amzn/meridian/modal';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import PlanStatusAlert from './PlanStatusAlert';
import MathematicalOptimizerModal from './MathematicalOptimizerModal';
import TimeDistributions from './TimeDistributions';
import usePlanPolling from './usePlanPolling';
import { createFlowPlan, approvePlan, rejectPlan, postOpportunities } from './flowPlannerApi';
import { OVERRIDE_KEYS, ERROR_MESSAGES, OPTIMIZER_PRESETS } from './flowPlannerConstants';
import { DEFAULT_MULTIPLIER } from './FlowPlannerGridConfig';
import { TEXTS, PLANS, PLAN_STATUS } from '../../utils/constants';
import { initializeModel, getCycleTotalVolume, computeAbsoluteVolumes } from './FlowPlannerModel';
import { getOptimizerPresets } from '../../utils/adminSettingsUtil';
import useRiskEvaluationPolling from './useRiskEvaluationPolling';
import ActiveRisksContainer from './ActiveRisksContainer';
import { RISK_ENGINE_PLAN_VERSIONS, isRiskScanning } from './riskEngineModel';
import {
  publishFlowPlannerCountMetric,
  publishFlowPlannerTimeMetric,
  FLOW_PLANNER_METRICS as M
} from '../../utils/metricUtil';
import './shared/flowPlannerShared.css';

const OVERRIDE_FIELDS = [
  'totalVolume',
  'volumeDistributions',
  'rateDistributions',
  'automationConfig',
  'cycleAutomationConfig',
  'supportHeadcountOverrides',
  'attendanceOverrides'
];
const OVERRIDE_METRIC_MAP = {
  totalVolume: M.OVERRIDE_VOLUME,
  volumeDistributions: M.OVERRIDE_VOLUME_DISTRIBUTION,
  rateDistributions: M.OVERRIDE_RATE_MULTIPLIER,
  automationConfig: M.OVERRIDE_AUTOMATION_CONFIG,
  cycleAutomationConfig: M.OVERRIDE_AUTOMATION_CONFIG,
  supportHeadcountOverrides: M.OVERRIDE_SUPPORT_HEADCOUNT,
  attendanceOverrides: M.OVERRIDE_ATTENDANCE
};

function snapshotOverrides(overrides) {
  const snapshot = {};
  OVERRIDE_FIELDS.forEach((key) => {
    snapshot[key] = JSON.stringify(overrides[key]);
  });
  return snapshot;
}

function emitOverrideMetrics(currentOverrides, initialSnapshot) {
  OVERRIDE_FIELDS.forEach((key) => {
    if (JSON.stringify(currentOverrides[key]) !== initialSnapshot[key]) {
      publishFlowPlannerCountMetric(OVERRIDE_METRIC_MAP[key]);
    }
  });
}
const FlowPlanner = ({
  date,
  responseDataForGivenPlan,
  planIdForGivenPlan,
  isOptimizerEnabled = false,
  isExperimentationMode = false,
  adminSettings = null
}) => {
  const [model, setModel] = useState(null);
  const [planStatus, setPlanStatus] = useState(null);
  const [planId, setPlanId] = useState('');
  const [planVersion, setPlanVersion] = useState('');
  const [error, setError] = useState(null);
  const [isOptimizerOpen, setIsOptimizerOpen] = useState(false);
  const [initialVolumeDistributions, setInitialVolumeDistributions] = useState(null);
  const [isVolDistDirty, setIsVolDistDirty] = useState(false);
  const [isPosted, setIsPosted] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [isPostModalOpen, setIsPostModalOpen] = useState(false);
  const [postResult, setPostResult] = useState(null);
  const initialOverridesRef = React.useRef(null);

  // Resolve presets from adminSettings (provided by parent), falling back to OPTIMIZER_PRESETS.
  // Used by the Math Optimizer modal in the NOT_RUN state, before any plan exists.
  const stationOptimizerPresets = useMemo(() => getOptimizerPresets(adminSettings, OPTIMIZER_PRESETS), [adminSettings]);

  const startPolling = usePlanPolling(responseDataForGivenPlan, setPlanStatus, setModel, setError);

  useEffect(() => {
    const v3DataStr = responseDataForGivenPlan?.[PLANS.FLOW_PLANNER];

    if (!v3DataStr) {
      setModel(null);
      setPlanStatus(null);
      setPlanId('');
      return;
    }

    try {
      const planData = JSON.parse(v3DataStr);

      if (
        planData.status === PLAN_STATUS.PENDING_CREATION ||
        planData.status === PLAN_STATUS.RECEIVED_USER_INPUT ||
        planData.status === PLAN_STATUS.PENDING_USER_INPUT
      ) {
        setPlanStatus(PLAN_STATUS.PENDING_CREATION);
        setPlanId(planData.planId || '');
        setModel(null);
        startPolling(planData.planId);
        return;
      }

      const initialized = initializeModel(v3DataStr);
      setModel(initialized);
      setInitialVolumeDistributions(JSON.stringify(initialized.overrides.volumeDistributions));
      setIsVolDistDirty(false);
      initialOverridesRef.current = snapshotOverrides(initialized.overrides);
      setPlanStatus(planData.status || null);
      setPlanId(planData.planId || planIdForGivenPlan?.[PLANS.FLOW_PLANNER] || '');
      setPlanVersion(planData.planVersion || '');
      setIsPosted(planData.status === PLAN_STATUS.APPROVED && Boolean(planData.isAutoPost));
    } catch (e) {
      publishFlowPlannerCountMetric(M.INIT_MODEL_FAILED);
      console.error('Flow Planner — Failed to initialize model:', e);
      setError(e.message);
    }
  }, [date, responseDataForGivenPlan, planIdForGivenPlan, startPolling]);

  // Keep planVersion in sync — responseDataForGivenPlan is a mutable ref so the
  // main useEffect may not re-run after polling updates it.
  useEffect(() => {
    if (!model) return;
    try {
      const dataStr = responseDataForGivenPlan?.[PLANS.FLOW_PLANNER];
      if (dataStr) {
        const parsed = JSON.parse(dataStr);
        setPlanVersion(parsed.planVersion || '');
        setPlanId(parsed.planId || '');
      }
    } catch (e) {
      // ignore parse errors
    }
  }, [model]);

  const updateSimpleOverride = useCallback((key, value) => {
    setModel((prev) => ({ ...prev, overrides: { ...prev.overrides, [key]: value } }));
  }, []);

  const handleTotalVolumeChange = useCallback(
    (v) => updateSimpleOverride(OVERRIDE_KEYS.TOTAL_VOLUME, v),
    [updateSimpleOverride]
  );
  const handleAutomationConfigChange = useCallback(
    (v) => updateSimpleOverride(OVERRIDE_KEYS.AUTOMATION_CONFIG, v),
    [updateSimpleOverride]
  );
  const handleCycleAutomationConfigChange = useCallback(
    (v) => updateSimpleOverride(OVERRIDE_KEYS.CYCLE_AUTOMATION_CONFIG, v),
    [updateSimpleOverride]
  );

  const handleDistributionsChange = useCallback((cycleName, process, newPctsArray) => {
    setIsVolDistDirty(true);
    setModel((prev) => {
      const cycleVol = prev.overrides.totalVolume.find((v) => v.name === cycleName);
      const total = cycleVol ? getCycleTotalVolume(cycleVol) : 0;
      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          volumeDistributions: {
            ...prev.overrides.volumeDistributions,
            [cycleName]: {
              ...prev.overrides.volumeDistributions[cycleName],
              [process]: { percentages: newPctsArray, volumes: computeAbsoluteVolumes(newPctsArray, total) }
            }
          }
        }
      };
    });
  }, []);

  const handleMultipliersChange = useCallback((cycle, processName, newMultipliers) => {
    setModel((prev) => {
      const processRate = (prev.inputs.processRates[cycle] || []).find((p) => p.name === processName);
      const targetRate = processRate?.targetRate || 0;
      return {
        ...prev,
        overrides: {
          ...prev.overrides,
          rateDistributions: {
            ...prev.overrides.rateDistributions,
            [cycle]: {
              ...prev.overrides.rateDistributions[cycle],
              [processName]: {
                ...prev.overrides.rateDistributions[cycle]?.[processName],
                percentages: newMultipliers,
                rates: newMultipliers.map((m) => Math.round(targetRate * (parseFloat(m) || 1)))
              }
            }
          }
        }
      };
    });
  }, []);

  const handleTargetRateChange = useCallback((cycle, processName, newRate) => {
    setModel((prev) => {
      const updatedRates = (prev.inputs.processRates[cycle] || []).map((p) =>
        p.name === processName ? { ...p, targetRate: newRate } : p
      );
      const currentMult =
        prev.overrides.rateDistributions[cycle]?.[processName]?.percentages ||
        Array(prev.inputs.intervals.length).fill(DEFAULT_MULTIPLIER);
      return {
        ...prev,
        inputs: {
          ...prev.inputs,
          processRates: { ...prev.inputs.processRates, [cycle]: updatedRates }
        },
        overrides: {
          ...prev.overrides,
          rateDistributions: {
            ...prev.overrides.rateDistributions,
            [cycle]: {
              ...prev.overrides.rateDistributions[cycle],
              [processName]: {
                percentages: currentMult,
                rates: currentMult.map((m) => Math.round(newRate * (parseFloat(m) || 1))),
                targetRate: newRate
              }
            }
          }
        }
      };
    });
  }, []);

  const handleSupportHeadcountChange = useCallback((cycle, processName, newHeadcounts) => {
    setModel((prev) => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        supportHeadcountOverrides: {
          ...prev.overrides.supportHeadcountOverrides,
          [cycle]: {
            ...prev.overrides.supportHeadcountOverrides?.[cycle],
            [processName]: newHeadcounts
          }
        }
      }
    }));
  }, []);

  const handleAttendanceChange = useCallback((intervalIndex, value) => {
    setModel((prev) => ({
      ...prev,
      overrides: {
        ...prev.overrides,
        attendanceOverrides: {
          ...prev.overrides.attendanceOverrides,
          [intervalIndex]: value
        }
      }
    }));
  }, []);

  const handleRunPlan = useCallback(async () => {
    const startTime = Date.now();
    publishFlowPlannerCountMetric(M.CALCULATE_PLAN);

    if (initialOverridesRef.current && model) {
      emitOverrideMetrics(model.overrides, initialOverridesRef.current);
      initialOverridesRef.current = snapshotOverrides(model.overrides);
    }

    setPlanStatus(PLAN_STATUS.PENDING_CREATION);
    setPlanId('');
    setPlanVersion('');
    setError(null);
    try {
      const res = await createFlowPlan(
        date,
        { ...model?.overrides, supportHeadcounts: model?.inputs?.supportHeadcounts },
        model?.rawInput,
        planId
      );
      publishFlowPlannerTimeMetric(M.CREATE_PLAN, startTime);
      if (!res?.planId) {
        publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
        setPlanStatus(PLAN_STATUS.FAILED);
        setError(ERROR_MESSAGES.CREATE_PLAN);
        return;
      }
      setPlanId(res.planId);
      planIdForGivenPlan[PLANS.FLOW_PLANNER] = res.planId;
      startPolling(res.planId, { fast: true, startTime });
    } catch (e) {
      publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
      setPlanStatus(PLAN_STATUS.FAILED);
      setError(e.message || ERROR_MESSAGES.CREATE_PLAN);
    }
  }, [model, date, planId, planIdForGivenPlan, startPolling]);

  const handleReplan = useCallback(async () => {
    const startTime = Date.now();
    publishFlowPlannerCountMetric(M.REPLAN);
    setPlanStatus(PLAN_STATUS.PENDING_CREATION);
    setPlanId('');
    setPlanVersion('');
    setError(null);
    try {
      const res = await createFlowPlan(date);
      publishFlowPlannerTimeMetric(M.REPLAN, startTime);
      if (!res?.planId) {
        publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
        setPlanStatus(PLAN_STATUS.FAILED);
        setError(ERROR_MESSAGES.CREATE_PLAN);
        return;
      }
      setPlanId(res.planId);
      planIdForGivenPlan[PLANS.FLOW_PLANNER] = res.planId;
      startPolling(res.planId, { startTime, e2eMetric: M.REPLAN_E2E });
    } catch (e) {
      publishFlowPlannerCountMetric(M.CREATE_PLAN_FAILED);
      setPlanStatus(PLAN_STATUS.FAILED);
      setError(e.message || ERROR_MESSAGES.CREATE_PLAN);
    }
  }, [date, planIdForGivenPlan, startPolling]);

  const handleStatusUpdate = useCallback(
    async (newStatus, errorMsg) => {
      if (!planId) return;
      const startTime = Date.now();
      const isApprove = newStatus === PLAN_STATUS.APPROVED;
      const apiFn = isApprove ? approvePlan : rejectPlan;
      try {
        await apiFn(planId);
        publishFlowPlannerTimeMetric(isApprove ? M.APPROVE_PLAN : M.REJECT_PLAN, startTime);
        publishFlowPlannerCountMetric(isApprove ? M.APPROVE_PLAN : M.REJECT_PLAN);
        setPlanStatus(newStatus);
      } catch (e) {
        publishFlowPlannerCountMetric(isApprove ? M.APPROVE_PLAN_FAILED : M.REJECT_PLAN_FAILED);
        setError(e.message || errorMsg);
      }
    },
    [planId]
  );

  const handlePostOpportunities = useCallback(async () => {
    if (!planId) return;
    setIsPosting(true);
    setPostResult(null);
    try {
      await postOpportunities(planId);
      setIsPosted(true);
      setPostResult({ success: true, message: 'Opportunities posted successfully' });
    } catch (e) {
      const raw = e?.errorMessage || e?.message || 'Failed to post opportunities';
      let message;
      try {
        message = JSON.parse(raw).message;
      } catch {
        message = raw;
      }
      setPostResult({ success: false, message });
    } finally {
      setIsPosting(false);
    }
  }, [planId]);

  const handleOptimizerUpdatePlan = useCallback(
    (optimizedPlanStr) => {
      try {
        responseDataForGivenPlan[PLANS.FLOW_PLANNER] = optimizedPlanStr;
        const planData = JSON.parse(optimizedPlanStr);
        const initialized = initializeModel(optimizedPlanStr);
        setModel(initialized);
        setInitialVolumeDistributions(JSON.stringify(initialized.overrides.volumeDistributions));
        setIsVolDistDirty(false);
        setPlanStatus(planData.status || null);
        setPlanId(planData.planId || '');
        setPlanVersion(planData.planVersion || '');
        setIsPosted(planData.status === PLAN_STATUS.APPROVED && Boolean(planData.isAutoPost));
        planIdForGivenPlan[PLANS.FLOW_PLANNER] = planData.planId || '';
      } catch (e) {
        console.error('Flow Planner — Failed to load optimized plan:', e);
        setError(e.message);
      }
    },
    [responseDataForGivenPlan, planIdForGivenPlan]
  );

  const isCreating = planStatus === PLAN_STATUS.PENDING_CREATION;
  const isPendingApproval =
    planStatus === PLAN_STATUS.PENDING_APPROVAL || planStatus === PLAN_STATUS.PENDING_USER_INPUT;

  const isApproved = planStatus === PLAN_STATUS.APPROVED;
  const isManualPostEnabled = Boolean(adminSettings?.manualPostEnabled);

  // Poll risk engine while pending approval or after approval (for post button).
  // Hook stops on terminal status (COMPLETED/FAILED) and resets on planId change.
  const shouldPollRiskEngine =
    (isPendingApproval || (isApproved && isManualPostEnabled)) && RISK_ENGINE_PLAN_VERSIONS.has(planVersion);
  const { riskEvaluation } = useRiskEvaluationPolling(planId, shouldPollRiskEngine);

  const isVolDistOverridden = isVolDistDirty;
  const isOpsClockEnded = useMemo(() => {
    const { nodeZoneId, opsClockEndTime } = model?.inputs || {};
    if (!nodeZoneId || !opsClockEndTime) return false;
    const now = new Date();
    const stationNow = new Date(now.toLocaleString('en-US', { timeZone: nodeZoneId }));
    const endTime = new Date(opsClockEndTime);
    return stationNow > endTime;
  }, [model]);
  const canOpenOptimizer = isOptimizerEnabled && !isVolDistOverridden && !isOpsClockEnded;

  const handleRevertVolumeDistributions = useCallback(() => {
    if (!initialVolumeDistributions) return;
    setModel((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, volumeDistributions: JSON.parse(initialVolumeDistributions) }
    }));
    setIsVolDistDirty(false);
  }, [initialVolumeDistributions]);

  if (!model || isCreating) {
    return (
      <Column spacingInset="400" alignmentVertical="top" width="100%">
        <PlanStatusAlert
          planStatus={planStatus}
          error={error}
          planId={planId}
          planVersion={planVersion}
          onRunPlan={handleRunPlan}
          onReplan={handleReplan}
          onOpenOptimizer={
            canOpenOptimizer
              ? () => {
                  publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                  setIsOptimizerOpen(true);
                }
              : null
          }
          timeDistributions={<TimeDistributions />}
          isExperimentationMode={isExperimentationMode}
        />
        {isCreating && (
          <Row alignmentHorizontal="center" spacingInset="600">
            <Loader size="large" />
          </Row>
        )}
        <MathematicalOptimizerModal
          isOpen={isOptimizerOpen}
          onClose={() => setIsOptimizerOpen(false)}
          date={date}
          model={model}
          dependentPlanId={planId}
          optimizerPresets={stationOptimizerPresets}
          onUpdatePlan={handleOptimizerUpdatePlan}
        />
      </Column>
    );
  }

  return (
    <Column spacingInset="400" alignmentVertical="top" alignmentHorizontal="stretch" width="100%">
      <PlanStatusAlert
        planStatus={planStatus}
        error={error}
        planId={planId}
        planVersion={planVersion}
        onRunPlan={handleRunPlan}
        onReplan={handleReplan}
        onOpenOptimizer={
          canOpenOptimizer
            ? () => {
                publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                setIsOptimizerOpen(true);
              }
            : null
        }
        timeDistributions={<TimeDistributions />}
        isExperimentationMode={isExperimentationMode}
      />

      <Box type="fill" spacingInset="400" style={{ width: '100%' }}>
        <Column spacing="400" alignmentHorizontal="stretch" width="100%">
          <Text type={TEXTS.H2}>Labor Planning Inputs</Text>
          <Text type={TEXTS.T1} color="secondary">
            Configure rates and volume distribution preferences
          </Text>

          <div className="fp-results-panel">
            <div className="fp-results-section">
              <VolumeConfiguration
                totalVolume={model.overrides.totalVolume}
                onTotalVolumeChange={handleTotalVolumeChange}
                joined
              />
            </div>
            <div className="fp-results-section">
              <AutomationConfiguration
                automationConfig={model.overrides.automationConfig}
                setAutomationConfig={handleAutomationConfigChange}
                availableCycles={model.inputs.availableCycles}
                cycleAutomationConfig={model.overrides.cycleAutomationConfig}
                isCycleLevelAutomation={model.overrides.isCycleLevelAutomation}
                onCycleAutomationConfigChange={handleCycleAutomationConfigChange}
                joined
              />
            </div>
            <div className="fp-results-section">
              <AttendanceOverride
                laborAvailability={model.inputs.laborAvailability}
                intervals={model.inputs.intervals}
                attendanceOverrides={model.overrides.attendanceOverrides || {}}
                onAttendanceChange={handleAttendanceChange}
                readOnly={planVersion === 'DYNAMIC_V2'}
                joined
              />
            </div>
            <div className="fp-results-section">
              <RateDistribution
                availableCycles={model.inputs.availableCycles}
                intervals={model.inputs.intervals}
                processRates={model.inputs.processRates}
                rateDistributions={model.overrides.rateDistributions}
                onMultipliersChange={handleMultipliersChange}
                onTargetRateChange={handleTargetRateChange}
                joined
              />
            </div>
            <div className="fp-results-section">
              <SupportHeadcount
                availableCycles={model.inputs.availableCycles}
                intervals={model.inputs.intervals}
                supportHeadcounts={model.inputs.supportHeadcounts}
                overrides={model.overrides.supportHeadcountOverrides || {}}
                onChange={handleSupportHeadcountChange}
                joined
              />
            </div>
            <div className="fp-results-section">
              <VolumeDistribution
                intervals={model.inputs.intervals}
                availableCycles={model.inputs.availableCycles}
                totalVolume={model.overrides.totalVolume}
                volumeDistributions={model.overrides.volumeDistributions}
                onDistributionsChange={handleDistributionsChange}
                processEvents={model.inputs.processEvents}
                isOverridden={!!isVolDistOverridden}
                onRevert={handleRevertVolumeDistributions}
                readOnly={planVersion === 'DYNAMIC_V2'}
                joined
              />
            </div>
          </div>
          <Box spacingInsetTop="400">
            <Column spacing="400" alignmentHorizontal="stretch" width="100%">
              {shouldPollRiskEngine && <ActiveRisksContainer riskEvaluation={riskEvaluation} />}
              <Row spacing="300" alignmentHorizontal="end" width="100%">
                {isOptimizerEnabled && (
                  <Button
                    type="secondary"
                    size="large"
                    onClick={() => {
                      publishFlowPlannerCountMetric(M.OPEN_OPTIMIZER);
                      setIsOptimizerOpen(true);
                    }}
                    disabled={isVolDistOverridden || isOpsClockEnded}>
                    Mathematical Optimizer
                  </Button>
                )}
                <Button type="primary" size="large" onClick={handleRunPlan}>
                  Calculate Plan
                </Button>
                {isPendingApproval && (
                  <>
                    <Button
                      type="primary"
                      size="large"
                      onClick={() => handleStatusUpdate(PLAN_STATUS.APPROVED, ERROR_MESSAGES.APPROVE_PLAN)}>
                      Approve
                    </Button>
                    <Button
                      type="tertiary"
                      size="large"
                      onClick={() => handleStatusUpdate(PLAN_STATUS.REJECTED, ERROR_MESSAGES.REJECT_PLAN)}>
                      Reject
                    </Button>
                  </>
                )}
                {(isPendingApproval || isApproved) && isManualPostEnabled && (
                  <Button
                    type="primary"
                    size="large"
                    disabled={!isApproved || isPosted || isRiskScanning(riskEvaluation)}
                    onClick={() => {
                      setPostResult(null);
                      setIsPostModalOpen(true);
                    }}>
                    {isPosted ? 'Posted' : 'Post Opportunities'}
                  </Button>
                )}
              </Row>
            </Column>
          </Box>
        </Column>
      </Box>

      <Modal
        title="Post Opportunities"
        open={isPostModalOpen}
        onClose={() => setIsPostModalOpen(false)}
        closeLabel="Close"
        width="900px">
        <Column spacing="300">
          <Text>The following opportunities will be posted to LSS:</Text>
          <Table headerRows={1} showDividers spacing="small">
            <TableRow>
              <TableCell>Type</TableCell>
              <TableCell>Headcount</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Workgroups</TableCell>
            </TableRow>
            {(model?.outputs?.laborOpportunities || []).map((opp, idx) => (
              <TableRow key={idx}>
                <TableCell>{opp.opportunityType}</TableCell>
                <TableCell>{opp.headcount || 0}</TableCell>
                <TableCell>
                  <span style={{ whiteSpace: 'nowrap' }}>{opp.applicability?.timeWindow?.start || '—'}</span>
                </TableCell>
                <TableCell>
                  <span style={{ whiteSpace: 'nowrap' }}>{opp.applicability?.timeWindow?.end || '—'}</span>
                </TableCell>
                <TableCell>{(opp.workgroupTags || []).join(', ')}</TableCell>
              </TableRow>
            ))}
          </Table>
          {postResult && (
            <Alert type={postResult.success ? 'success' : 'error'} size="medium">
              {postResult.message}
            </Alert>
          )}
          {!postResult && (
            <Text type="b200">
              <em>Clicking Post will publish opportunities to LSS immediately.</em>
            </Text>
          )}
          <Row alignmentHorizontal="end" spacing="200">
            {!postResult && (
              <>
                <Button type="secondary" onClick={() => setIsPostModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="primary" disabled={isPosting} onClick={handlePostOpportunities}>
                  {isPosting ? 'Posting...' : 'Post'}
                </Button>
              </>
            )}
            {postResult && (
              <Button type="primary" onClick={() => setIsPostModalOpen(false)}>
                Close
              </Button>
            )}
          </Row>
        </Column>
      </Modal>

      <ResultsView
        gridData={model.outputs?.gridData || null}
        laborSummary={model.outputs?.laborSummary || null}
        availableCycles={model.inputs.availableCycles}
        intervals={model.inputs.intervals}
        laborAvailabilityChart={model.outputs?.laborAvailabilityChart || null}
        requiredHCPerInterval={model.outputs?.requiredHCPerInterval || []}
        availableHCPerInterval={model.outputs?.availableHCPerInterval || []}
        paidHoursPerInterval={model.outputs?.paidHoursPerInterval || []}
        opportunityHCPerInterval={model.outputs?.opportunityHCPerInterval || null}
        volumeWIPChart={model.outputs?.volumeWIPChart || null}
        laborOpportunities={model.outputs?.laborOpportunities || []}
        planVersion={planVersion}
      />

      <MathematicalOptimizerModal
        isOpen={isOptimizerOpen}
        onClose={() => setIsOptimizerOpen(false)}
        date={date}
        model={model}
        dependentPlanId={planId}
        optimizerPresets={stationOptimizerPresets}
        onUpdatePlan={handleOptimizerUpdatePlan}
      />
    </Column>
  );
};

export default FlowPlanner;
